import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { enviarContacto } from "@/lib/api";

const VACIO = { nombre: "", email: "", telefono: "", mensaje: "" };

export const ContactModal = ({ open, onOpenChange, prefill = "" }) => {
  const [form, setForm] = useState(VACIO);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...VACIO, mensaje: prefill || "" });
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await enviarContacto(form);
      setStatus("success");
      toast.success("Mensaje enviado correctamente. Te responderemos muy pronto.");
    } catch {
      setStatus("error");
      setErrorMsg("No se pudo enviar el mensaje. Revisa los campos e inténtalo de nuevo.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-modal"
        className="rounded-3xl sm:max-w-md"
      >
        {status === "success" ? (
          <div
            data-testid="contact-modal-success"
            className="flex flex-col items-center py-8 text-center"
          >
            <CheckCircle2 className="h-14 w-14 text-green-700" strokeWidth={1.4} />
            <h3 className="mt-6 font-serif text-2xl text-tinta">Mensaje enviado</h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-tinta/65">
              Gracias por escribirnos. Te responderemos lo antes posible.
            </p>
            <Button
              data-testid="contact-modal-success-close"
              onClick={() => onOpenChange(false)}
              className="mt-8 rounded-full px-8"
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-tinta">
                Contactar
              </DialogTitle>
              <DialogDescription className="text-sm text-tinta/60">
                Cuéntanos en qué podemos ayudarte y te responderemos lo antes posible.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="mt-5 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cm-nombre">Nombre</Label>
                <Input
                  id="cm-nombre"
                  data-testid="contact-modal-nombre"
                  value={form.nombre}
                  onChange={set("nombre")}
                  placeholder="Tu nombre"
                  required
                  minLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cm-email">Email</Label>
                <Input
                  id="cm-email"
                  data-testid="contact-modal-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cm-telefono">Teléfono</Label>
                <Input
                  id="cm-telefono"
                  data-testid="contact-modal-telefono"
                  type="tel"
                  value={form.telefono}
                  onChange={set("telefono")}
                  placeholder="+34 600 000 000"
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cm-mensaje">Mensaje</Label>
                <Textarea
                  id="cm-mensaje"
                  data-testid="contact-modal-mensaje"
                  value={form.mensaje}
                  onChange={set("mensaje")}
                  placeholder="Cuéntanos en qué podemos ayudarte"
                  rows={3}
                  required
                  minLength={10}
                />
              </div>
              {status === "error" && (
                <p
                  data-testid="contact-modal-error"
                  role="alert"
                  className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                >
                  {errorMsg}
                </p>
              )}
              <Button
                type="submit"
                data-testid="contact-modal-submit"
                disabled={status === "sending"}
                className="w-full rounded-full py-6 text-base"
              >
                {status === "sending" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Enviar mensaje
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
